import {adminCustomPagesRoutes} from '@common/admin/custom-pages/admin-custom-pages-routes';
import {adminFileEntriesRoutes} from '@common/admin/file-entry/admin-file-entries-routes';
import {adminLogsRoutes} from '@common/admin/logging/admin-logs-routes';
import {adminRolesRoutes} from '@common/admin/roles/admin-roles-routes';
import {commonAdminSettingsRoutes} from '@common/admin/settings/common-admin-settings-routes';
import {adminBillingRoutes} from '@common/admin/subscriptions/admin-billing-routes';
import {adminTagsRoutes} from '@common/admin/tags/admin-tags-routes';
import {adminLocalizationsRoutes} from '@common/admin/translations/admin-localizations-routes';
import {adminUsersRoutes} from '@common/admin/users/admin-users-routes';
import {authGuard} from '@common/auth/guards/auth-route';
import {Navigate, redirect, RouteObject} from 'react-router';
import {getBootstrapData} from '@ui/bootstrap-data/bootstrap-data-store';

function adminOnlyGuard() {
  const user = getBootstrapData().user;
  if (user?.type === 'super_admin') {
    return redirect('/admin/space-overview');
  }
  return null;
}

function AdminErrorFallback() {
  return (
    <div className="flex min-h-screen items-center justify-center">
      <div className="text-center">
        <h2 className="mb-8 text-xl font-semibold">Page could not load</h2>
        <p className="mb-16 text-muted">Your session may have expired.</p>
        <div className="flex justify-center gap-8">
          <button
            className="rounded-button bg-primary px-16 py-8 text-sm font-medium text-on-primary"
            onClick={() => window.location.reload()}
          >
            Refresh Page
          </button>
          <button
            className="rounded-button border px-16 py-8 text-sm font-medium"
            onClick={() => window.location.href = '/stellar_drive/login'}
          >
            Go to Login
          </button>
        </div>
      </div>
    </div>
  );
}

function AdminIndexRedirect() {
  const user = getBootstrapData().user;
  // Super admin goes to client management, admin goes to my-users
  if (user?.type === 'super_admin') {
    return <Navigate to="space-overview" replace />;
  }
  return <Navigate to="my-users" replace />;
}

export const adminRoutes: RouteObject[] = [
  {
    path: 'admin',
    loader: () => authGuard({permission: 'admin.access'}),
    lazy: () => import('@common/admin/admin-layout'),
    errorElement: <AdminErrorFallback />,
    children: [
      {
        index: true,
        element: <AdminIndexRedirect />,
      },
      {
        path: 'reports',
        lazy: () => import('@app/admin/analytics/app-analytics-page'),
      },
      // Super Admin: space overview
      {
        path: 'space-overview',
        lazy: () =>
          import(
            '@app/admin/space-management/admin-space-dashboard'
          ),
      },
      {
        path: 'space-overview/:adminId',
        lazy: () =>
          import(
            '@app/admin/space-management/admin-detail-page'
          ),
      },
      // Admin: manage own users
      {
        path: 'my-users',
        lazy: () =>
          import(
            '@app/admin/space-management/managed-users-page'
          ),
      },
      // File Browser (admin only — super admin should not see user files)
      {
        path: 'user-files',
        loader: () => adminOnlyGuard(),
        lazy: () =>
          import(
            '@app/admin/file-browser/admin-file-browser-page'
          ),
      },
      // Reports
      {
        path: 'file-reports',
        lazy: () =>
          import(
            '@app/admin/reports/admin-reports-page'
          ),
      },
      // Activity Logs
      {
        path: 'activity-logs',
        lazy: () =>
          import(
            '@app/admin/activity-logs/activity-logs-page'
          ),
      },
      // Share Approvals (admin only — super admin should not see approvals)
      {
        path: 'approvals',
        loader: () => adminOnlyGuard(),
        lazy: () =>
          import(
            '@app/admin/approvals/approvals-page'
          ),
      },
      // Notifications
      {
        path: 'notifications',
        lazy: () =>
          import(
            '@app/admin/notifications/admin-notifications-page'
          ),
      },
      ...Object.values(adminUsersRoutes).map(route => ({
        ...route,
        loader: (args: any) => {
          const guard = adminOnlyGuard();
          if (guard) return guard;
          return typeof route.loader === 'function' ? route.loader(args) : null;
        },
      })),
      ...Object.values(adminRolesRoutes).map(route => ({
        ...route,
        loader: (args: any) => {
          const guard = adminOnlyGuard();
          if (guard) return guard;
          return typeof route.loader === 'function' ? route.loader(args) : null;
        },
      })),
      ...Object.values(adminBillingRoutes),
      ...Object.values(adminCustomPagesRoutes),
      ...Object.values(adminTagsRoutes),
      ...Object.values(adminLocalizationsRoutes),
      ...Object.values(adminFileEntriesRoutes),
      ...Object.values(adminLogsRoutes).map(route => ({
        ...route,
        loader: (args: any) => {
          const guard = adminOnlyGuard();
          if (guard) return guard;
          return typeof route.loader === 'function' ? route.loader(args) : null;
        },
      })),

      commonAdminSettingsRoutes(
        [
          {
            path: 'drive',
            lazy: () => import('@app/admin/settings/drive-settings'),
          },
          {
            path: 'landing-page',
            lazy: () =>
              import(
                '@common/admin/settings/landing-page-settings/landing-page-settings'
              ),
          },
          {
            path: 'ads',
            lazy: () => import('@common/admin/settings/pages/ads-settings'),
          },
        ],
        {
          uploading: {
            lazy: () =>
              import(
                '@common/admin/settings/pages/uploading-settings/uploading-settings'
              ),
          },
          general: {
            lazy: () => import('@app/admin/settings/app-general-settings'),
          },
        },
      ),
    ],
  },
];
